/**
 * ComparisonFieldSection Component
 *
 * Collapsible section that groups comparison field rows.
 * Used to organize fields by form_section from metadata.
 */

import { Collapse, Badge, Typography, Space } from 'antd'
import { CaretRightOutlined } from '@ant-design/icons'
import type { FieldMetadata, FormSection } from '@/services/metadataService'
import type { FieldDifference } from './types'
import { ComparisonFieldRow } from './ComparisonFieldRow'

const { Text } = Typography

/**
 * Convert section name to title case display.
 */
function formatSectionTitle(name: string): string {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export interface ComparisonFieldSectionProps {
  /** Section information from metadata */
  section: FormSection
  /** Field differences for this section */
  differences: FieldDifference[]
  /** Field metadata lookup */
  fields: Record<string, FieldMetadata>
  /** Key fields set from comparison config */
  keyFields?: Set<string>
  /** Whether section is expanded */
  expanded?: boolean
  /** Callback when section is expanded/collapsed */
  onExpandChange?: (expanded: boolean) => void
  /** Compact mode for smaller displays */
  compact?: boolean
  /** Whether a comparison record is selected (false = show "--" placeholder) */
  hasComparison?: boolean
}

/**
 * Collapsible section grouping comparison field rows.
 *
 * @example
 * ```tsx
 * <ComparisonFieldSection
 *   section={{ name: 'pricing', order: 1 }}
 *   differences={pricingDiffs}
 *   fields={metadata.fields}
 *   keyFields={new Set(['price', 'cost'])}
 *   expanded={!collapsedSections.has('pricing')}
 *   onExpandChange={(exp) => toggleSection('pricing')}
 * />
 * ```
 */
export function ComparisonFieldSection({
  section,
  differences,
  fields,
  keyFields = new Set(),
  expanded = true,
  onExpandChange,
  compact = false,
  hasComparison = true,
}: ComparisonFieldSectionProps): JSX.Element {
  // Count changes in this section
  const changedCount = differences.filter(d => d.isDifferent).length
  const totalCount = differences.length

  // Section title
  const title = formatSectionTitle(section.name)

  // Handle collapse change
  const handleCollapseChange = (keys: string | string[]) => {
    const isExpanded = Array.isArray(keys) ? keys.includes(section.name) : keys === section.name
    onExpandChange?.(isExpanded)
  }

  const headerContent = (
    <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
      <Space>
        <Text strong style={{ fontSize: compact ? 13 : 14 }}>
          {title}
        </Text>
        {section.description && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({section.description})
          </Text>
        )}
      </Space>
      <Space>
        {changedCount > 0 ? (
          <Badge
            count={changedCount}
            style={{
              backgroundColor: 'var(--rp-temporal-orange)',
              fontSize: 11,
            }}
            title={`${changedCount} of ${totalCount} fields changed`}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {totalCount} fields
          </Text>
        )}
      </Space>
    </Space>
  )

  return (
    <Collapse
      activeKey={expanded ? [section.name] : []}
      onChange={handleCollapseChange}
      expandIcon={({ isActive }) => (
        <CaretRightOutlined
          rotate={isActive ? 90 : 0}
          style={{ fontSize: 12 }}
          aria-hidden="true"
        />
      )}
      style={{
        marginBottom: compact ? 8 : 12,
        backgroundColor: 'var(--rp-surface-primary)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      items={[
        {
          key: section.name,
          label: headerContent,
          children: (
            <div
              role="region"
              aria-label={`${title} field comparisons`}
              style={{
                margin: '-12px -16px',
              }}
            >
              {differences.length === 0 ? (
                <div
                  style={{
                    padding: '16px',
                    textAlign: 'center',
                  }}
                >
                  <Text type="secondary">No fields in this section</Text>
                </div>
              ) : (
                <div role="list" aria-label={`${changedCount} of ${totalCount} fields changed`}>
                  {differences.map(diff => (
                    <ComparisonFieldRow
                      key={diff.fieldKey}
                      difference={diff}
                      metadata={fields[diff.fieldKey]}
                      isKeyField={keyFields.has(diff.fieldKey)}
                      compact={compact}
                      hasComparison={hasComparison}
                    />
                  ))}
                </div>
              )}
            </div>
          ),
          style: {
            borderBottom: 'none',
          },
        },
      ]}
    />
  )
}

/**
 * Group differences by form section.
 *
 * @param differences - All field differences
 * @param fields - Field metadata lookup
 * @param formSections - Form sections from metadata
 * @returns Differences grouped by section name
 */
export function groupDifferencesBySection(
  differences: FieldDifference[],
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[]
): Map<string, FieldDifference[]> {
  // Create section map with default "other" section
  const sections = new Map<string, FieldDifference[]>()

  // Initialize sections from metadata
  for (const section of formSections) {
    sections.set(section.name, [])
  }

  // Always have an "other" section for uncategorized fields
  sections.set('other', [])

  // Group differences by section
  for (const diff of differences) {
    const fieldMeta = fields[diff.fieldKey]
    const sectionName = fieldMeta?.form_section || 'other'

    if (!sections.has(sectionName)) {
      sections.set(sectionName, [])
    }

    sections.get(sectionName)!.push(diff)
  }

  // Remove empty sections (except "other" if it has items)
  for (const [key, diffs] of sections) {
    if (diffs.length === 0 && key !== 'other') {
      sections.delete(key)
    }
  }

  // Remove "other" if empty
  if (sections.get('other')?.length === 0) {
    sections.delete('other')
  }

  return sections
}

/**
 * Get ordered form sections including "other" at the end.
 *
 * @param formSections - Form sections from metadata
 * @param sectionGroups - Grouped differences to know which sections have content
 * @returns Ordered array of form sections
 */
export function getOrderedSections(
  formSections: FormSection[],
  sectionGroups: Map<string, FieldDifference[]>
): FormSection[] {
  // Get sections that have content, in order
  const orderedSections = formSections
    .filter(s => sectionGroups.has(s.name))
    .sort((a, b) => a.order - b.order)

  // Add "other" section at the end if it exists
  if (sectionGroups.has('other')) {
    orderedSections.push({
      name: 'other',
      order: 999,
      description: 'Additional Fields',
    })
  }

  return orderedSections
}

export default ComparisonFieldSection
